import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingBag, Users, Sparkles, BarChart3, MessageSquare, Camera, Briefcase, Gift, UserCog, LogOut, ExternalLink, BellRing, CreditCard } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const LOGO = "https://customer-assets.emergentagent.com/job_f80e39fa-3ab4-4fff-9ec3-40cb3c2b407a/artifacts/imxdvq2l_ChatGPT%20Image%20May%2023%2C%202026%2C%2012_15_06%20PM.png";

function LiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30 * 1000);
    return () => clearInterval(id);
  }, []);
  const fmt = (opts) => new Intl.DateTimeFormat("en-IN", { timeZone: "Asia/Kolkata", ...opts }).format(now);
  const day = fmt({ weekday: "short" });
  const date = fmt({ day: "2-digit", month: "short", year: "numeric" });
  const time = fmt({ hour: "numeric", minute: "2-digit", hour12: true });
  return (
    <div className="label-mono text-[#71717A] hidden sm:block" data-testid="admin-clock">
      {day.toUpperCase()} <span className="text-white/70">·</span> {date.toUpperCase()} <span className="text-white/70">·</span> <span className="text-[#CFF048]">{time} IST</span>
    </div>
  );
}

export default function AdminLayout({ children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pendingReviews, setPendingReviews] = useState(0);
  const [pendingPosts, setPendingPosts] = useState(0);
  const [newBulk, setNewBulk] = useState(0);
  const [restockWaiting, setRestockWaiting] = useState(0);
  const [newOrders, setNewOrders] = useState(0);

  useEffect(() => {
    let mounted = true;
    const fetchCounts = async () => {
      try {
        const [rev, com, bulk, restock, orders] = await Promise.all([
          api.get("/admin/reviews/counts"),
          api.get("/admin/community/counts"),
          api.get("/admin/bulk/counts"),
          api.get("/admin/restock-enquiries/counts"),
          api.get("/admin/orders"),
        ]);
        if (!mounted) return;
        setPendingReviews(rev.data?.pending || 0);
        setPendingPosts(com.data?.pending || 0);
        setNewBulk(bulk.data?.new || 0);
        setRestockWaiting(restock.data?.waiting || 0);
        const placed = (orders.data || []).filter((o) => o.status === "placed").length;
        setNewOrders(placed);
      } catch { /* ignore */ }
    };
    fetchCounts();
    const id = setInterval(fetchCounts, 60 * 1000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  const navs = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/products", label: "Products", icon: Package },
    { to: "/admin/orders", label: "Orders", icon: ShoppingBag, badge: newOrders },
    { to: "/admin/payments", label: "Payments", icon: CreditCard },
    { to: "/admin/bulk", label: "Bulk Orders", icon: Briefcase, badge: newBulk },
    { to: "/admin/restock", label: "Restock", icon: BellRing, badge: restockWaiting },
    { to: "/admin/affiliates", label: "Affiliates", icon: Gift },
    { to: "/admin/reviews", label: "Reviews", icon: MessageSquare, badge: pendingReviews },
    { to: "/admin/community", label: "Community", icon: Camera, badge: pendingPosts },
    { to: "/admin/users", label: "Users", icon: Users },
    { to: "/admin/reports", label: "Reports", icon: BarChart3 },
    { to: "/admin/mockup", label: "AI Generator", icon: Sparkles },
    { to: "/admin/profile", label: "My Profile", icon: UserCog },
  ];
  return (
    <div className="min-h-screen bg-black text-white grid grid-cols-1 md:grid-cols-[260px_1fr]" data-testid="admin-layout">
      <aside className="border-r border-[#27272A] bg-[#0A0A0A] hidden md:flex flex-col">
        <Link to="/admin" className="px-6 py-5 border-b border-[#27272A] flex items-center gap-3">
          <img src={LOGO} alt="Cult Lyf" className="h-9 w-9" />
          <div>
            <div className="font-display font-black tracking-tight">CULT LYF</div>
            <div className="label-mono text-[#CFF048] text-[10px]">CONTROL ROOM</div>
          </div>
        </Link>
        <nav className="flex-1 py-4">
          {navs.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) => `admin-nav-item ${isActive ? "active" : ""}`}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(" ", "-")}`}
            >
              <n.icon size={16} />
              <span>{n.label}</span>
              {n.badge ? (
                <span
                  className="ml-auto bg-[#CFF048] text-black text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-sm"
                  data-testid={`admin-nav-${n.label.toLowerCase().replace(" ", "-")}-badge`}
                >
                  {n.badge}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="flex flex-col">
        <header className="h-14 border-b border-[#27272A] flex items-center justify-between px-4 sm:px-6 gap-3" data-testid="admin-header">
          <h1 className="font-display font-black tracking-tight text-base sm:text-lg uppercase truncate">{title || "Admin"}</h1>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link
              to="/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-1.5 label-mono text-[#71717A] hover:text-[#CFF048] transition-colors border border-[#27272A] hover:border-[#CFF048] px-3 py-1.5"
              data-testid="admin-view-website-btn"
              title="View website"
            >
              <ExternalLink size={13} /> VIEW WEBSITE
            </Link>
            <LiveClock />
            <div className="hidden md:flex items-center gap-2 border-l border-[#27272A] pl-3" data-testid="admin-user-info">
              <div className="w-8 h-8 bg-[#CFF048] text-black font-display font-black text-xs flex items-center justify-center">
                {(user?.name || user?.email || "A")[0].toUpperCase()}
              </div>
              <div className="leading-tight">
                <p className="font-display font-bold text-xs truncate max-w-[140px]">{user?.name || "Admin"}</p>
                <p className="label-mono text-[#71717A] truncate max-w-[140px]">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={() => { logout(); navigate("/"); }}
              className="flex items-center gap-1.5 label-mono text-white hover:text-[#CFF048] transition-colors border border-[#27272A] hover:border-[#CFF048] px-3 py-1.5"
              data-testid="admin-logout-btn"
              title="Logout"
            >
              <LogOut size={13} /> <span className="hidden sm:inline">LOGOUT</span>
            </button>
          </div>
        </header>
        <main className="p-6 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
